import React, { useMemo } from 'react';
import useIntl from '../../../hooks/useIntl';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { FolderOpenIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Box,
  Button,
  SubmitButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';

import messages from './messages';
import { useGenerateResidentLetter } from './hooks';

type Props = {
  show: boolean,
  onClose: Function,
  letters: Array<any>,
  titleText?: string,
  buttonText?: string,
  isAllCommercial?: boolean,
  applicationId?: string,
  residentId?: string,
  customerName?: string,
  propertyId?: string,
  organizationId?: string,
  onSuccess?: Function,
  onSelection?: Function, // For template selection without generation
  // Legacy props for backward compatibility
  intl?: Object, // Optional since we use useIntl internally
  isAffordable?: boolean, // Kept for backward compatibility
};

const getFormFields = (letters, formatMessage, isAllCommercial) => [
  {
    variant: 'formLabel',
    children: isAllCommercial
      ? formatMessage(messages.tenantSelectDocuments)
      : formatMessage(messages.selectDocuments),
    GridProps: { xs: 12 },
  },
  {
    variant: 'radioGroup',
    name: 'residentLetterId',
    items: letters.map(({ text, value: letterValue }) => ({
      value: letterValue,
      label: text,
    })),
    rules: { required: true },
    GridProps: { xs: 12 },
  },
];

export const ResidentLettersModal = ({
  show,
  onClose,
  letters: letterOptions,
  titleText,
  buttonText,
  isAllCommercial,
  applicationId,
  residentId,
  customerName,
  propertyId,
  organizationId,
  onSuccess,
  onSelection,
  intl: intlProp, // Legacy prop - not used since we use useIntl internally
  isAffordable,
}: Props) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const ReactHookFormProps = useReactHookFormProps({
    defaultValues: {
      residentLetterId: '',
    },
  });
  const { handleSubmit, reset, isValid } = ReactHookFormProps;

  const generateLetterMutation = useGenerateResidentLetter({
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
      handleClose();
    },
  });

  const letters = useMemo(
    () =>
      letterOptions.sort((a, b) =>
        a.text.localeCompare(b.text, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      ),
    [letterOptions],
  );

  const formFields = useMemo(
    () => getFormFields(letters, formatMessage, isAllCommercial),
    [letters, formatMessage, isAllCommercial],
  );

  const title = titleText
    ? titleText
    : isAllCommercial
    ? formatMessage(messages.tenantLetterLibrary)
    : formatMessage(messages.residentLetterLibrary);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data) => {
    // If onSelection is provided, this is template selection mode
    if (onSelection) {
      const selectedLetter = letters.find(
        (letter) => letter.value === data.residentLetterId,
      );
      onSelection({
        residentLetterId: data.residentLetterId,
        letter: selectedLetter,
      });
      handleClose();
      return;
    }

    // Otherwise, generate the letter
    const selectedLetter = letters.find(
      (letter) => letter.value === data.residentLetterId,
    );
    const filename = `${customerName || 'resident'}_${
      selectedLetter?.text || 'letter'
    }.pdf`;

    generateLetterMutation.mutate({
      applicationId,
      residentId,
      residentLetterId: data.residentLetterId,
      filename,
      organizationId,
      propertyId,
    });
  };

  const areButtonsDisabled = useMemo(
    () => !onSelection && generateLetterMutation.isLoading,
    [onSelection, generateLetterMutation.isLoading],
  );

  return (
    <Modal
      open={show}
      onClose={handleClose}
      icon={<FolderOpenIcon />}
      title={title}
      hideActions
      ModalProps={{
        PaperProps: {
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 480,
            },
            maxHeight: 'calc(100vh - 100px)',
          },
        },
      }}
    >
      <Box>
        <Form
          ReactHookFormProps={ReactHookFormProps}
          fields={formFields}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
            <Button
              variant="text"
              onClick={handleClose}
              disabled={areButtonsDisabled}
            >
              {formatMessage(messages.cancel)}
            </Button>
            <SubmitButton
              variant="primary"
              type="submit"
              disabled={!isValid || areButtonsDisabled}
              isSubmitting={areButtonsDisabled}
            >
              {buttonText || formatMessage(messages.generateLetter)}
            </SubmitButton>
          </Box>
        </Form>
      </Box>
    </Modal>
  );
};

export default ResidentLettersModal;
